import { useCallback, useMemo } from 'react'

import { useRouter } from 'next/router'

import { indexes } from '@mab-common/utils'

import AnimateIntoView, {
  AnimationType,
} from '~/components/atoms/AnimateIntoView/AnimateIntoView'
import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import Container from '~/components/atoms/Container/Container'
import { Col, ContainerTypes, Grid, Row } from '~/components/atoms/Grid'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import { BannerTheme } from '~/enums/banner-theme'
import type {
  TSearchAnalystEventParams,
  TSearchAnalystEventProps,
} from '~/services/elastic/analyst'
import useSearchAnalyst from '~/services/elastic/analyst'
import { useMediaQuery } from '~/services/useMediaQuery'
import bp from '~/styles/breakpoints/breakpoints'

import styles from './AdvantagesBanner.module.css'
import EsIndexTypes = indexes.EsIndexTypes
import useFormAnalytics from '~/hooks/useFormAnalytics'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import type {
  PushInteractionLocation,
  PushProperties,
} from '~/utils/posthogEvents'

export interface AdvantagesBannerProps {
  testID?: string
  advantagesTitle: string
  advantagesSummary: string
  advantagesItems: string[]
  advantagesButtonText: string
  advantagesTerms?: string
  theme: BannerTheme
  artistId?: string
  collectionId?: string
  searchInsightsEvent?: TSearchAnalystEventProps
  analyticsInteractionLocation?: PushInteractionLocation
  analyticsCtaSource?: PushProperties['ctaSource']
}

export default function AdvantagesBanner({
  testID,
  advantagesTitle: title,
  advantagesSummary: summary,
  advantagesItems: items,
  advantagesButtonText: buttonText,
  advantagesTerms: terms,
  theme,
  artistId,
  collectionId,
  searchInsightsEvent,
  analyticsInteractionLocation,
  analyticsCtaSource,
}: AdvantagesBannerProps) {
  const router = useRouter()
  const isMobile = useMediaQuery(`(max-width: ${bp.bpMediumMax})`)
  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const { handleFormLaunchFromCtaAnalytics } = useFormAnalytics()

  const displayTitle = useMemo(() => {
    // Wait for router to be ready before processing
    if (!router.isReady) {
      return title
    }

    if (
      title.toLowerCase() === 'sell your art with us' ||
      title.toLowerCase() === 'sell your art <br/> with us'
    ) {
      // Remove query string from asPath
      const pathname = router.asPath.split('?')[0]
      const segments = pathname.split('/')

      const artistSegment = segments.find(seg => seg.startsWith('artist-'))

      if (artistSegment) {
        const artistName = artistSegment.substring(7).replace(/-/g, ' ')

        return title
          .replace(/\bart\b/i, artistName)
          .replace('<br/>', ' ')
          .replace('<br />', ' ')
      }
    }
    return title
  }, [title, router.asPath, router.isReady])

  const searchInsightsEventParams: Omit<
    TSearchAnalystEventParams,
    'eventName'
  > | null = useMemo(() => {
    if (artistId)
      return {
        indexType: EsIndexTypes.ARTIST,
        filters: [`artistId:${artistId}`],
      }
    if (collectionId)
      return {
        indexType: EsIndexTypes.ARTWORKS,
        filters: [`collectionId:${collectionId}`],
      }
    return null
  }, [artistId, collectionId])

  const onButtonClick = useCallback(async () => {
    await multiPostHogEventPusher([
      {
        eventType:
          searchInsightsEvent?.name ||
          `Advantages Banner: ${buttonText} clicked`,
      },
    ])

    if (searchInsightsEvent && searchInsightsEventParams) {
      sendSearchInsightsEvent(searchInsightsEvent?.method, {
        ...searchInsightsEventParams,
        eventName: searchInsightsEvent?.name,
      })
    }

    if (analyticsCtaSource) {
      handleFormLaunchFromCtaAnalytics(
        'Enquire for Valuation Clicked',
        FormTypes.valuation,
        analyticsCtaSource,
        undefined,
        undefined,
        undefined,
        undefined,
        analyticsInteractionLocation
      )
    }
  }, [
    searchInsightsEvent,
    buttonText,
    searchInsightsEventParams,
    analyticsCtaSource,
    sendSearchInsightsEvent,
    handleFormLaunchFromCtaAnalytics,
    analyticsInteractionLocation,
  ])

  const renderButton = () => {
    let buttonVariant = ButtonVariants.secondaryDark
    if (theme === BannerTheme.dark) buttonVariant = ButtonVariants.primaryLight

    return (
      <div className={styles.buttonContainer}>
        <Button
          iconLeft={true}
          variant={buttonVariant}
          className={styles.button}
          launchForm={FormTypes.valuation}
          button={{
            onClick: onButtonClick,
            type: ButtonTypes.button,
          }}
        >
          {buttonText}
        </Button>
        {isMobile && renderTerms()}
      </div>
    )
  }

  const renderTerms = () =>
    terms && (
      <div className={styles.ternsContainer}>
        <Text size={TextSizes.bodySml} className={styles.terms}>
          {terms}
        </Text>
      </div>
    )

  return (
    <div
      className={styles[theme]}
      data-testid={testID}
      data-test="advantages-banner"
    >
      <Container>
        <Grid variant={ContainerTypes.NoGutters}>
          <Row>
            <Col variant="col-2/3--md">
              <div className={styles.content}>
                <AnimateIntoView type={AnimationType.fadeIn}>
                  <div className={styles.contentInner}>
                    <Text
                      Tag="h2"
                      size={TextSizes.primaryTitleLrger}
                      className={styles.title}
                    >
                      <div
                        dangerouslySetInnerHTML={{
                          __html: displayTitle,
                        }}
                        key={displayTitle}
                      />
                    </Text>
                    <Text
                      Tag="p"
                      size={TextSizes.secondaryTitleLrg}
                      className={styles.body}
                    >
                      {summary}
                    </Text>
                    {!isMobile && renderButton()}
                  </div>
                </AnimateIntoView>
                {!isMobile && renderTerms()}
              </div>
            </Col>
            <Col variant="col-1/3--md">
              <div className={styles.itemsContainer}>
                <div className={styles.items}>
                  {items &&
                    items.map((item, key) => (
                      <Text
                        key={`advantage-banner-${key}`}
                        className={styles.item}
                        size={TextSizes.primaryTitleSml}
                      >
                        {item}
                      </Text>
                    ))}
                </div>
                {isMobile && renderButton()}
              </div>
            </Col>
          </Row>
        </Grid>
      </Container>
    </div>
  )
}
